<template>
  <div>
    <payment-notices />
    <collapsible-section class='_pt0 _pb0' :collapsed='false'>
      <template #headerText>
        <p class='color-leaf'>Automatic Payments</p>
      </template>
      <template #headerActions>
        <async-button
          v-if="currentPaymentMethod.paymentMethodId && !$isInternalUser"
          :executing="deletingPaymentMethod"
          @clicked="onDeletePaymentMethod"
          class="btn sun white --sm _mlxs"
        >
          Delete
        </async-button>
        <a
          v-if="currentPaymentMethod.paymentMethodId && !cybersourceDetailsLoading && !$isInternalUser"
          href="javascript:void(0);"
          @click="onEditPaymentMethod"
          class="btn sun white --sm"
        >
          Edit
        </a>
      </template>
      <template #collapsibleSection>

        <div class="_pbxs">
          <async-loader v-if='paymentMethodsLoading'/>
          <div
              v-else-if="currentPaymentMethod.paymentMethodId"
              container
              class='_ptxs _pbxs'
            >
              <div row class='_flex'>
                <labeled-data label='Payment Type:' :data='currentPaymentMethod.paymentType.toUpperCase()'/>
                <labeled-data label='Payment Account:' :data='accountLastFour'/>
                <labeled-data label='Expiration Date:' :data='expirationDate'/>
                <labeled-data label='Set Up Date:' :data='currentPaymentMethod.createDate'/>
                <labeled-data label='Added By:' :data='currentPaymentMethod.createUser'/>
              </div>
            </div>
            <div
              v-else-if="!currentPaymentMethod.paymentMethodId"
              container
              class='_ptxs _pbxs _flex justify-center align-center _flex-column'
            >
              <h4>No payment methods are currently available.</h4>
              <div class="aggreement-container">
                <app-checkbox
                  :value="agreeToTerms"
                  @click="toggleAggreement($event)"
                  valueKey='agreeToTerms'
                  label='' />
                <div class="agreement-text">
                  <div class="_mbxs" v-html="add_payment_method_disclaimer_html"></div>
                </div>
              </div>
              <button
                v-if="!$isInternalUser"
                type="button"
                class="btn sun white --sm"
                @click="onAddPaymentMethod"
                :disabled="!agreeToTerms"
                >
                Add Payment Method
              </button>
            </div>

          <!-- No payment method to configure autopay with -->
          <div
            v-if="!paymentMethodsLoading && !currentPaymentMethod.paymentMethodId && !paymentMethodsLoading"
            container
            class='_ptxs _pbxs _flex justify-center align-center _flex-column'
          >
            <h5 class="color-black">Payment method on file is required in order to setup Automatic Payments.</h5>
          </div>

          <!-- Allow user to configure autopayment for their current payment method -->
          <div
            v-else
            container
          >
            <div
              v-if="autoPayEnabled">
              <div grid>
                <div column class="_text-center">
                  <async-loader class="small" v-if='!recurring_payments_enabled_html'/>
                  <h5 v-else class="color-black" v-html="interpolateWithRecurringPaymentDate(recurring_payments_enabled_html)"></h5>
                </div>
              </div>
              <div grid class="_text-center">
                <div column/>
                <div column>
                  <labeled-data
                    label="Automatic Payments Enabled By:"
                    :data="currentPaymentMethod.modifyUser"
                  />
                </div>
                <div column>
                  <labeled-data
                    label="Automatic Payments Enabled On:"
                    :data="currentPaymentMethod.modifyDate"
                  />
                </div>
                <div column/>
              </div>
            </div>
            <div v-else class="_text-center color-black">
              <async-loader class="small" v-if='!recurring_payment_setup_prompt_html'/>
              <h5 v-else class="color-black" v-html="interpolateWithRecurringPaymentDate(recurring_payment_setup_prompt_html)"></h5>
            </div>
            <div>
              <async-loader class="small" v-if='!debited_date_notice_html'/>
              <p v-else class="_text-center" v-html="interpolateWithRecurringPaymentDate(debited_date_notice_html)"></p>
            </div>
            <div row class="_flex align-center justify-center _pts _w100">
              <tool-tip
                v-if="$isInternalUser"
                bottom
                message="Internal users cannot enable/disable auto-pay.">
                <async-button
                  :disabled="true"
                  :executing="configuringRecurringPayments"
                  class="btn sun white --sm"
                  @clicked="submit"
                >
                  {{ autoPayEnabled ? 'Disable Auto-Pay' : 'Enable Auto-Pay' }}
                </async-button>
              </tool-tip>

              <div v-else>
                <div class="aggreement-container"
                  v-if="!autoPayEnabled"
                >
                  <app-checkbox
                    :value="agreeToTerms"
                    @click="toggleAggreement($event)"
                    valueKey='agreeToAutoPayTerms'
                    label='' />
                  <div class="agreement-text">
                    <div class="_mbxs" v-html="enable_recurring_payment_disclaimer_html"></div>
                  </div>
                </div>
                <div class="_text-center">
                  <async-button
                    :executing="configuringRecurringPayments"
                    class="btn sun white --sm"
                    :disabled="!autoPayEnabled && !agreeToTerms"
                    @clicked="submit"
                  >
                    {{ autoPayEnabled ? 'Disable Auto-Pay' : 'Enable Auto-Pay' }}
                  </async-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </collapsible-section>
  </div>
</template>

<script>
  import AppCheckbox from '@atoms/Checkbox'
  import AsyncLoader from '@molecules/AsyncLoader'
  import AsyncButton from '@molecules/AsyncButton'
  import CollapsibleSection from '@sections/CollapsibleSection'
  import billing from '@mixins/billing'
  import paymentVerbiage from '@mixins/paymentVerbiage'
  import { mapState } from 'vuex'
  import LabeledData from '@molecules/LabeledData'
  import ToolTip from '@molecules/ToolTip'
  import PaymentNotices from '../PaymentNotices'
  import { formatDate } from '@/utils'

  export default {
    mixins: [billing, paymentVerbiage],
    components: {
      AppCheckbox,
      ToolTip,
      LabeledData,
      CollapsibleSection,
      AsyncButton,
      AsyncLoader,
      PaymentNotices
    },
    props: {
      onAddPaymentMethod: {
        type: Function
      },
      onEditPaymentMethod: {
        type: Function
      },
      onDeletePaymentMethod: {
        type: Function
      }
    },
    computed: {
      ...mapState({
        configuringRecurringPayments: state => state.billing.configuringRecurringPayments,
        deletingPaymentMethod: state => state.billing.deletingPaymentMethod
      }),
      accountLastFour() {
        if (this.currentPaymentMethod.lastFour) {
          return 'xxxx' + this.currentPaymentMethod.lastFour
        } else {
          return this.currentPaymentMethod.accountNumber
        }
      },
      expirationDate() {
        if (this.currentPaymentMethod.paymentType.toUpperCase() === 'CREDIT CARD') {
          return formatDate(this.currentPaymentMethod.expirationMonYear, 'MMM YYYY')
        } else {
          return 'N/A'
        }
      }
    },
    data () {
      return {
        yes: true,
        no: false,
        debited_date_notice_html: '',
        recurring_payment_setup_prompt_html: '',
        recurring_payments_enabled_html: '',
        recurring_payment_day_of_month: '',
        agreeToTerms: false
      }
    },
    methods: {
      interpolateWithRecurringPaymentDate(html) {
        return html.replace(/\{\{ ?date ?\}\}/g, this.recurring_payment_day_of_month)
      },
      submit () {
        this.$store.dispatch('SETUP_RECURRING_PAYMENTS', {
          body: {
            paymentMethodId: this.currentPaymentMethod.paymentMethodId,
            autoPayment: !this.autoPayEnabled
          }
        })
          .then((success) => {
            this.$store.dispatch('GET_PAYMENT_METHODS')
          })
      },
      toggleAggreement(value) {
        this.agreeToTerms = value
      }
    }
  }
</script>
